import React from 'react';

import autoBind from 'react-autobind';
import { IconHelp, IconSearch, IconFocusCentered, IconRoute } from '@tabler/icons-react';

import Localization from "../../Localization.jsx"

import { Combobox, InputBase, useCombobox, Button, Group, Tooltip } from '@mantine/core';

// Location search box built on Mantine's low-level Combobox instead of Autocomplete.
// The geocoding results arrive asynchronously (debounce + Photon/Nominatim network round-trip)
// AFTER the keystroke. Mantine's Autocomplete only opens its dropdown in response to the user
// event, so those late-arriving results landed in a closed dropdown and never appeared. Here we
// drive the dropdown's open state off the results themselves, so suggestions show as soon as
// they arrive.
function LocationSearchBox({ label, options, getOptionLabel, onSearch, onSelect }) {
  const combobox = useCombobox();
  const [value, setValue] = React.useState('');
  const safeOptions = Array.isArray(options) ? options : [];

  React.useEffect(() => {
    if (safeOptions.length > 0) {
      combobox.openDropdown();
    } else {
      combobox.closeDropdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const comboOptions = safeOptions.map((option) => {
    const optionLabel = getOptionLabel(option);
    return (
      <Combobox.Option value={optionLabel} key={option.id || optionLabel}>
        {optionLabel}
      </Combobox.Option>
    );
  });

  return (
    <Combobox
      store={combobox}
      zIndex={3000}
      onOptionSubmit={(submittedLabel) => {
        setValue(submittedLabel);
        combobox.closeDropdown();
        const selected = safeOptions.find((option) => getOptionLabel(option) === submittedLabel);
        if (selected) {
          onSelect(selected);
        }
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          value={value}
          onChange={(event) => {
            const next = event.currentTarget.value;
            setValue(next);
            onSearch(next);
          }}
          onFocus={() => { if (safeOptions.length > 0) { combobox.openDropdown(); } }}
          onClick={() => { if (safeOptions.length > 0) { combobox.openDropdown(); } }}
          onBlur={() => combobox.closeDropdown()}
          size="sm"
          style={{ width: 250 }}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {comboOptions}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

const DEFAULT_CENTER = { lat: 22.4191, lng: 114.2059 };
const HK_SEARCH_CENTER = { lat: 22.3198, lng: 114.1639 };
const HK_VIEWBOX = '113.76,22.15,114.51,22.56';
const PHOTON_API_URL = 'https://photon.komoot.io/api/';
const NOMINATIM_API_URL = 'https://nominatim.openstreetmap.org/search';
const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_RESULT_LIMIT = 5;
const SEARCH_FETCH_LIMIT = 12;

class LocaleDetailMapBar extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    this.state = {
      mapSizeMessage: "",
      mapSizeReminder: "",
      autocompleteOptions: [],
    };
    autoBind(this);

    this.lastSearchID = 0;
    this.searchDebounceTimeout = null;
    this.fetchPredictions = this.fetchPredictions.bind(this);
  }

  componentWillUnmount() {
    clearTimeout(this.searchDebounceTimeout);
  }

  getLocaleCenter() {
    const center = this.props.parent?.state?.localeCenter;
    if (center && Number.isFinite(center.lat) && Number.isFinite(center.lng)) {
      return center;
    }
    return DEFAULT_CENTER;
  }

  getSearchCenter() {
    const center = this.getLocaleCenter();
    const hasMovedFromDefault =
      Math.abs(center.lat - DEFAULT_CENTER.lat) > 0.01 ||
      Math.abs(center.lng - DEFAULT_CENTER.lng) > 0.01;

    if (hasMovedFromDefault) {
      return center;
    }
    return HK_SEARCH_CENTER;
  }

  getAcceptLanguage() {
    const lang = Localization.strings.getLanguage();
    if (lang === 'tc') {
      return 'zh-HK,zh-Hant,zh';
    }
    if (lang === 'sc') {
      return 'zh-CN,zh-Hans,zh';
    }
    return 'en';
  }

  formatLocationLabel(parts) {
    const seen = new Set();
    return parts
      .map((part) => (part || '').trim())
      .filter((part) => {
        if (!part || seen.has(part)) {
          return false;
        }
        seen.add(part);
        return true;
      })
      .join(', ');
  }

  mapPhotonFeatureToOption(feature) {
    const properties = feature.properties || {};
    const coordinates = feature.geometry?.coordinates || [];
    const description = this.formatLocationLabel([
      properties.name,
      properties.street,
      properties.district,
      properties.city,
      properties.state,
      properties.country,
    ]);

    return {
      id: `photon-${properties.osm_type || 'x'}-${properties.osm_id || description}`,
      description: description || properties.name || '',
      center: coordinates,
      provider: 'osm-photon',
      raw: feature,
    };
  }

  mapNominatimResultToOption(result) {
    return {
      id: `nominatim-${result.osm_type || 'x'}-${result.osm_id || result.place_id}`,
      description: result.display_name || '',
      center: [parseFloat(result.lon), parseFloat(result.lat)],
      provider: 'osm-nominatim',
      raw: result,
    };
  }

  getOptionName(option) {
    if (option.provider === 'osm-photon') {
      return option.raw?.properties?.name || option.description || '';
    }
    if (option.provider === 'osm-nominatim') {
      return option.raw?.name || option.description.split(',')[0] || '';
    }
    return option.description || '';
  }

  getDedupeKeys(option) {
    const description = (option.description || '').trim().toLowerCase();
    const name = this.getOptionName(option).trim().toLowerCase();
    const lng = Number(option.center[0]).toFixed(4);
    const lat = Number(option.center[1]).toFixed(4);
    const keys = [option.id, description];

    if (name) {
      keys.push(`${name}@${lng},${lat}`);
    }

    return keys.filter(Boolean);
  }

  deduplicateSearchOptions(options) {
    const seenKeys = new Set();
    const uniqueOptions = [];

    for (const option of options) {
      const keys = this.getDedupeKeys(option);
      const isDuplicate = keys.some((key) => seenKeys.has(key));
      if (isDuplicate) {
        continue;
      }

      keys.forEach((key) => seenKeys.add(key));
      uniqueOptions.push(option);

      if (uniqueOptions.length >= SEARCH_RESULT_LIMIT) {
        break;
      }
    }

    return uniqueOptions;
  }

  async searchPhoton(query) {
    const center = this.getSearchCenter();
    const params = new URLSearchParams({
      q: query,
      limit: String(SEARCH_FETCH_LIMIT),
      lat: String(center.lat),
      lon: String(center.lng),
    });

    const response = await fetch(`${PHOTON_API_URL}?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`Photon search failed: ${response.status}`);
    }

    const data = await response.json();
    return (data.features || []).map((feature) => this.mapPhotonFeatureToOption(feature));
  }

  async searchNominatim(query) {
    const params = new URLSearchParams({
      q: query,
      format: 'json',
      limit: String(SEARCH_FETCH_LIMIT),
      addressdetails: '1',
      viewbox: HK_VIEWBOX,
      bounded: '0',
      'accept-language': this.getAcceptLanguage(),
    });

    const response = await fetch(`${NOMINATIM_API_URL}?${params.toString()}`, {
      headers: {
        'Accept': 'application/json',
        'Accept-Language': this.getAcceptLanguage(),
      },
    });

    if (!response.ok) {
      throw new Error(`Nominatim search failed: ${response.status}`);
    }

    const data = await response.json();
    return (data || []).map((result) => this.mapNominatimResultToOption(result));
  }

  async runLocationSearch(query) {
    let options = await this.searchPhoton(query);

    if (options.length === 0) {
      options = await this.searchNominatim(query);
    }

    const validOptions = options.filter(
      (option) => option.description && option.center?.length === 2
    );
    return this.deduplicateSearchOptions(validOptions);
  }

  fetchLocationPredictions(value) {
    const searchID = ++this.lastSearchID;
    this.runLocationSearch(value)
      .then((options) => {
        if (searchID !== this.lastSearchID) {
          return;
        }
        this.setState({ autocompleteOptions: options });
      })
      .catch((err) => {
        console.log("OpenStreetMap geocoding failed", err);
        if (searchID === this.lastSearchID) {
          this.setState({ autocompleteOptions: [] });
        }
      });
  }

  fetchPredictions = (value) => {
    clearTimeout(this.searchDebounceTimeout);

    if (!value || !value.trim()) {
      this.setState({ autocompleteOptions: [] });
      return;
    }

    this.searchDebounceTimeout = setTimeout(() => {
      this.fetchLocationPredictions(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  getOptionLabel(option) {
    if (typeof option === 'string') {
      return option;
    }
    return option?.description || '';
  }

  isOptionEqualToValue(option, value) {
    if (!option || !value) {
      return false;
    }
    if (option.id && value.id) {
      return option.id === value.id;
    }
    return option.description === value.description;
  }

  onSuggestionSelected = (event, suggestion) => {
    if (!suggestion?.center || suggestion.center.length < 2) {
      return;
    }

    const coordinates = {
      lat: suggestion.center[1],
      lng: suggestion.center[0],
    };
    this.props.parent.mapPanTo(coordinates);
  };

  updateMapSize() {
    if (this.props.parent.state.viewState && this.props.parent.state.localeRect){
      let zoom = this.props.parent.state.viewState.zoom;

      let bounds = this.props.parent.state.localeRect.bounds;
      let upperLeft = this.props.parent.latLonToMeter(bounds.ul.lat, bounds.ul.lon);
      let bottomRight = this.props.parent.latLonToMeter(bounds.br.lat, bounds.br.lon);
      let width = parseInt(Math.abs(bottomRight.x - upperLeft.x));
      let height = parseInt(Math.abs(upperLeft.y - bottomRight.y));
      let message = width + "m * " + height + "m";
    }
    if (this.props.parent.state.localeRect) {
      let bounds = this.props.parent.state.localeRect.bounds;
      let upperLeft = this.props.parent.latLonToMeter(bounds.ul.lat, bounds.ul.lon);
      let bottomRight = this.props.parent.latLonToMeter(bounds.br.lat, bounds.br.lon);
      let width = parseInt(Math.abs(bottomRight.x - upperLeft.x));
      let height = parseInt(Math.abs(upperLeft.y - bottomRight.y));
      let message = width + "m * " + height + "m";

      let reminder = "";
      if (width > this.props.parent.MaxLocaleMapSize() || height > this.props.parent.MaxLocaleMapSize()) {
        reminder = Localization.strings.LOCALE_MAP_MAX_SIZE_REMINDER;
      }
      else if (width < this.props.parent.MinLocaleMapSize() || height < this.props.parent.MinLocaleMapSize()) {
        reminder = Localization.strings.LOCALE_MAP_MIN_SIZE_REMINDER;
      }

      this.setState({
        mapSizeMessage: message,
        mapSizeReminder: reminder,
      });
      this.state.mapSizeMessage = message;
      this.state.mapSizeReminder = reminder;
    }
  }

  render() {
    return (
      <Group gap="xs" align="center">
        <Group gap="xs" align="flex-end">
          <IconSearch size={20} style={{ marginBottom: "6px" }} />
          <LocationSearchBox
            label={Localization.strings.LOCATION_SEARCH}
            options={this.state.autocompleteOptions}
            getOptionLabel={this.getOptionLabel}
            onSearch={this.fetchPredictions}
            onSelect={(option) => this.onSuggestionSelected(null, option)}
          />
        </Group>
        <Group gap={0} align="center">
          <Button
            onClick={this.props.parent.localeMatchViewClicked}
            leftSection={<IconFocusCentered size={18} />}
          >
            {Localization.strings.DEFINE_FIELD_TRIP_AREA}
          </Button>
          <Tooltip label={Localization.strings.MATCHVIEW_REMINDER} position="bottom-end">
            <IconHelp size={18} style={{ color: '#e0e0e0', marginLeft: '8px' }} />
          </Tooltip>
        </Group>
        <Group gap={0} align="center">
          <Button
            onClick={this.props.parent.localeTeleportClicked}
            disabled={this.props.isTeleportButtonDisabled}
            leftSection={<IconRoute size={18} />}
          >
            {Localization.strings.TELEPORT}
          </Button>
          <Tooltip label={Localization.strings.TELEPORT_REMINDER} position="bottom-end">
            <IconHelp size={18} style={{ color: '#e0e0e0', marginLeft: '8px' }} />
          </Tooltip>
        </Group>
      </Group>
    )
  }
}

export default LocaleDetailMapBar;
